import React, { useEffect } from 'react'
import { Alert, Group, Stack, Text } from '@mantine/core'

import { ExhibitRow } from './ExhibitRow'

import { ActionButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionButton'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionTitle'
import { useScriptFlowState } from '@/components/Event/NewScript/hooks/state/useScriptFlowState'
import { Event } from '@/types'
import { getEventTimezone } from '@/utils/time'

export const ExhibitsSection: React.FC<{ event: Event }> = ({ event }) => {
  const { actions, currentEvent } = useScriptFlowState()
  const timezone = getEventTimezone(event)

  const exhibits = currentEvent?.exhibits ?? []
  const noExhibits = currentEvent?.confirmed_no_exhibits ?? false
  // Showing the section means the reporter has been asked about exhibits, so
  // an empty list must read as "not completed" rather than "not collected".
  // Once tracked this turns false, so the effect fires once per witness.
  const needsTracking =
    currentEvent !== undefined &&
    ((currentEvent.exhibits ?? null) === null ||
      (currentEvent.confirmed_no_exhibits ?? null) === null)

  useEffect(() => {
    if (needsTracking) {
      actions.ensureExhibitsTracked()
    }
  }, [needsTracking, actions])

  return (
    <Stack
      // The questioning step is wrapped in a form, where Enter in a single-line
      // input would submit it and run its validation.
      onKeyDown={event => {
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
          event.preventDefault()
        }
      }}
    >
      <ActionTitle>Exhibits</ActionTitle>
      {exhibits.length === 0 ? (
        noExhibits ? (
          <Alert
            color="blue"
            sx={theme => ({
              backgroundColor: theme.colors.indigo[0],
              border: 'none',
            })}
          >
            <Stack spacing="sm" align="flex-start">
              <Text size="sm">
                You selected{' '}
                <Text span fw={700}>
                  No Exhibits Presented
                </Text>{' '}
                for this witness. If any exhibit was presented use button below.
              </Text>
              <ActionButton onClick={() => actions.addExhibit()}>
                Exhibits Presented
              </ActionButton>
            </Stack>
          </Alert>
        ) : (
          <Stack>
            <ActionText>
              Were any exhibits presented during this witness's testimony?
            </ActionText>
            <Group>
              <ActionButton onClick={() => actions.setNoExhibits(true)}>
                No Exhibits Presented
              </ActionButton>
              <ActionButton onClick={() => actions.addExhibit()}>
                Exhibits Presented
              </ActionButton>
            </Group>
          </Stack>
        )
      ) : (
        <Stack>
          {exhibits.map(exhibit => (
            <ExhibitRow
              key={exhibit.id}
              exhibit={exhibit}
              timezone={timezone}
              onChange={patch =>
                actions.updateExhibit({ id: exhibit.id, patch })
              }
              onDelete={() => actions.deleteExhibit(exhibit.id)}
            />
          ))}
          <Group>
            <ActionButton onClick={() => actions.addExhibit()}>
              Add exhibit
            </ActionButton>
          </Group>
        </Stack>
      )}
    </Stack>
  )
}
